import * as React from 'react'
import { PublicPlan } from '@/components/lazyDrawings'
import { SetupInstructions } from '@/components/public/SetupInstructions'
import { timeRangeLabel } from '@/lib/eventTime'
import { hasSetupContent } from '@lib/helpers/setup-markdown'
import type { PublicPhaseDto, PublicSeatingDto, PublicSetPieceDto } from '@lib/dto/public-schedule.dto'
import { chairCountLabel, hasSeating, seatingHeadline, tableCountLabel } from '@/lib/phaseSeating'

/** Whose piece it is, in the words a crew would use. */
const SOURCE_LABEL: Record<PublicSetPieceDto['source'], string> = {
  venue: 'Venue',
  client: 'Brought in',
  vendor: 'Vendor',
}

/**
 * What to put in the space, and whose it is.
 *
 * The source is the column that earns its place: a crew that cannot tell the
 * venue's own stock from a client's van will either set out furniture that has
 * not arrived or leave somebody's chairs in the room.
 */
function SetList({ pieces }: { pieces: PublicSetPieceDto[] }): React.ReactElement {
  if (pieces.length === 0) {
    return <p className="m-0 text-sm text-ink-muted">No furniture is placed in this space.</p>
  }
  return (
    <ul className="m-0 list-none divide-y divide-line overflow-hidden rounded-lg border border-line bg-surface p-0">
      {pieces.map((piece) => (
        <li key={`${piece.name}-${piece.source}`} className="flex items-baseline justify-between gap-4 px-4 py-2.5">
          <span className="text-sm text-ink">{piece.name}</span>
          <span className="shrink-0 text-sm text-ink-muted">
            {SOURCE_LABEL[piece.source]} · <span className="font-mono tabular-nums text-ink">×{piece.count}</span>
          </span>
        </li>
      ))}
    </ul>
  )
}

/**
 * The room's tables and the chairs drawn at each, saying whose they are as the
 * set list does, then any chairs at no table: what a caterer sets places for (docs/vendor-growth.spec.md, "What vendors on
 * an event see").
 */
function Seating({ seating }: { seating: PublicSeatingDto }): React.ReactElement {
  return (
    <div className="space-y-2">
      <h3 className="m-0 text-sm font-semibold text-ink">Tables and seats</h3>
      <p className="m-0 text-sm text-ink">{seatingHeadline(seating)}</p>
      <ul className="m-0 list-none divide-y divide-line overflow-hidden rounded-lg border border-line bg-surface p-0">
        {seating.tables.map((row) => (
          <li key={`${row.name}-${row.source}-${String(row.chairsEach)}`} className="flex items-baseline justify-between gap-4 px-4 py-2.5">
            <span className="text-sm text-ink">{row.name}</span>
            <span className="shrink-0 text-sm tabular-nums text-ink-muted">
              {SOURCE_LABEL[row.source]} · {tableCountLabel(row)}
            </span>
          </li>
        ))}
        {seating.chairsNotAtTable === 0 ? null : (
          <li className="flex items-baseline justify-between gap-4 px-4 py-2.5">
            <span className="text-sm text-ink">Chairs not at a table</span>
            <span className="shrink-0 text-sm tabular-nums text-ink-muted">{chairCountLabel(seating.chairsNotAtTable)}</span>
          </li>
        )}
      </ul>
    </div>
  )
}

/**
 * One room of an event and its plan, drawn read-only with what goes in it: the
 * crew's public page and a vendor's view of an event show it the same way, so
 * the two cannot come to describe one plan differently.
 */
export function PlanPhase({ phase }: { phase: PublicPhaseDto }): React.ReactElement {
  const times = timeRangeLabel(phase.startsAt, phase.endsAt)
  return (
    <section className="public-space space-y-4">
      <div>
        <h2 className="m-0 text-lg font-semibold text-ink">{phase.spaceName}</h2>
        {times === '' ? null : <p className="m-0 font-mono text-sm tabular-nums text-ink-muted">{times}</p>}
      </div>

      <PublicPlan
        spaceName={phase.spaceName}
        width={phase.width}
        height={phase.height}
        zones={phase.zones}
        staticLabels={phase.staticLabels}
        objects={phase.objects}
      />

      {/* The parser decides, not a trim: a box holding only "- " trims to
          something and renders to nothing, and a heading over nothing is worse
          than no heading. */}
      {hasSetupContent(phase.instructions) ? (
        <div className="space-y-2">
          <h3 className="m-0 text-sm font-semibold text-ink">Instructions</h3>
          <SetupInstructions source={phase.instructions ?? ''} />
        </div>
      ) : null}

      {hasSeating(phase.seating) ? <Seating seating={phase.seating} /> : null}

      <div className="space-y-2">
        <h3 className="m-0 text-sm font-semibold text-ink">What goes in</h3>
        <SetList pieces={phase.setList} />
      </div>
    </section>
  )
}
